// Saving pictures, videos and GIFs from chat (FR "Add ability to download photos or gifs"). Files come from Bloom's own
// route with ?download=1 (an attachment, with its name); KLIPY GIFs through /api/gif-file, since a browser won't name
// or save a file from another site. On phones the share sheet comes first, so "Save Image" puts it in Photos; where
// that isn't possible, a plain download.
import type { Attachment } from "@bloom/shared";
import { fileUrl, isImage, isVideo } from "./upload.ts";

export interface Downloadable {
  url: string;
  name: string;
}

/** What "Download" saves for an attachment: pictures, videos and GIFs (other files already download on click). */
export function downloadable(a: Attachment): Downloadable | null {
  if (a.kind === "gif") {
    const ext = a.format === "video" ? "mp4" : (/\.(gif|webp|png|jpe?g)(?:$|\?)/i.exec(a.url)?.[1]?.toLowerCase() ?? "gif");
    const base = (a.title || "gif").replace(/[\\/:*?"<>|]+/g, " ").trim().slice(0, 80) || "gif";
    const name = `${base}.${ext}`;
    return { url: `/api/gif-file?url=${encodeURIComponent(a.url)}&name=${encodeURIComponent(name)}`, name };
  }
  if (a.kind === "file" && (isImage(a.type) || isVideo(a.type))) return { url: `${fileUrl(a.key)}?download=1`, name: a.name };
  return null;
}

/**
 * Where the page reads the bytes of a picker GIF (#589): its own file when that is on our origin (the dev fake KLIPY),
 * else /api/gif-file, since KLIPY's host is neither in connect-src nor known to send CORS headers. The same route
 * saves a GIF; the browser keeps the answer for a day.
 */
export function gifBytesUrl(url: string): string {
  try {
    if (new URL(url, location.href).origin === location.origin) return url;
  } catch {
    // a url that does not parse goes to the route, which says no
  }
  return `/api/gif-file?url=${encodeURIComponent(url)}`;
}

/** The message's attachments that can be downloaded. */
export const downloadables = (attachments: Attachment[] | undefined): Downloadable[] =>
  (attachments ?? []).map(downloadable).filter((d): d is Downloadable => !!d);

const touch = () => typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;

/** Saves them: the share sheet on phones (Save Image / Save Video), else a download each. */
export async function download(items: Downloadable[]): Promise<void> {
  if (!items.length) return;
  if (touch() && typeof navigator.share === "function" && typeof navigator.canShare === "function") {
    try {
      const files = await Promise.all(
        items.map(async (d) => {
          const res = await fetch(d.url, { credentials: "same-origin" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          return new File([blob], d.name, { type: blob.type });
        }),
      );
      if (navigator.canShare({ files })) {
        await navigator.share({ files });
        return;
      }
    } catch (e) {
      // Closing the share sheet isn't a failure; anything else (no gesture left, a type it won't share) falls back.
      if (e instanceof DOMException && e.name === "AbortError") return;
    }
  }
  for (const [i, d] of items.entries()) {
    const link = document.createElement("a");
    link.href = d.url;
    link.download = d.name;
    link.rel = "noopener";
    document.body.append(link);
    link.click();
    link.remove();
    // Browsers drop rapid-fire downloads; a little room between them.
    if (i < items.length - 1) await new Promise((r) => setTimeout(r, 300));
  }
}
